import React, { useState, useEffect } from "react";
import { Block } from "baseui/block";
import { HeadingMedium } from "baseui/typography";
import { Textarea, SIZE as TSIZE } from "baseui/textarea";
import { FormControl } from "baseui/form-control";
import { Select, SIZE as SSIZE } from "baseui/select";
import { Button, KIND, SHAPE, SIZE } from "baseui/button";
import { FaSave } from "react-icons/fa";
import { Tabs, Tab } from "baseui/tabs-motion";
import { ProgressBar } from "baseui/progress-bar";
import { connect } from "react-redux";
import useToaster from "../../hooks/useToaster";
import { updateModeler, executeReportPipeline } from "../../redux/modeler/ModelerThunk";
import { addReportRunAndFetchAssets, removeReportRun, updateReportRunSelectedAssets } from "../../redux/model-run/ModelRunThunk";
import * as ModelRunActions from "../../redux/model-run/ModelRunActions";
import ModelRunItem from "../../components/model-run-item/ModelRunItem";

function ModelerReportsView(props) {
  const [reportContent, setReportContent] = useState(
    props.activeModeler?.report || ""
  );
  const [selectedModelRun, setSelectedModelRun] = useState(null);
  const [activeKey, setActiveKey] = useState("0");
  const [isContentModified, setIsContentModified] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasExecutionStarted, setHasExecutionStarted] = useState(false);
  
  // Use the enhanced toaster hook for different message types
  useToaster(props.publishReportErrorMessage, 'error');
  useToaster(props.fetchReportAssetsErrorMessage, 'error');
  
  // Track execution lifecycle to show toast only for actual executions
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Reset execution tracking when component mounts or when status changes to NONE
  useEffect(() => {
    if (props.publishReportStatus === "NONE" || props.publishReportStatus === null) {
      setHasExecutionStarted(false);
    }
  }, [props.publishReportStatus]);
  
  // Track when execution starts
  useEffect(() => {
    if (props.publishReportStatus === "STARTED") {
      setHasExecutionStarted(true);
    }
  }, [props.publishReportStatus]);
  
  // Show success toast only if execution was started and completed successfully
  useEffect(() => {
    if (hasExecutionStarted && 
        props.publishReportStatus === "SUCCESS" && 
        props.activeModeler?.generatedReportId) {
      
      setSuccessMessage(`Report generated successfully!`);
      setHasExecutionStarted(false); // Reset to prevent showing again
      
      // Clear the message after a short delay
      setTimeout(() => setSuccessMessage(null), 100);
    }
  }, [hasExecutionStarted, props.publishReportStatus, props.activeModeler?.generatedReportId]);
  
  useToaster(successMessage, 'success');
  
  // Get selected report runs from Redux
  const selectedModelRuns = Object.values(props.reportRuns || {});

  useEffect(() => {
    setReportContent(props.activeModeler?.report || "");
    setIsContentModified(false);
  }, [props.activeModeler]);





  // Prepare model runs for the combobox
  const modelRunOptions = props.activeModeler?.modelRuns?.items?.map(run => ({
    id: run.id,
    label: run.runName,
    value: run.id
  })) || [];

  const handleContentChange = (e) => {
    const newValue = e.target.value;
    setReportContent(newValue);
    setIsContentModified(newValue !== (props.activeModeler?.report || ""));
  };

  const handleSave = async () => {
    if (!isContentModified || isSaving) return;
    
    setIsSaving(true);
    try {
      await props.updateActiveModelerProperties(props.activeModeler?.id, {
        report: reportContent,
      });
      setIsContentModified(false);
      console.log("Report content saved successfully");
    } catch (error) {
      console.error("Error saving report content:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBlur = () => {
    // Remove auto-save on blur since we now have an explicit save button
    // if (reportContent !== props.activeModeler?.report) {
    //   props.updateActiveModelerProperties(props.activeModeler?.id, {
    //     report: reportContent,
    //   });
    // }
  };

  const handleModelRunSelect = async (params) => {
    if (params.value && params.value.length > 0) {
      const selectedRun = params.value[0];
      setSelectedModelRun(selectedRun);
      
      // Check if already in report runs
      const runExists = props.reportRuns[selectedRun.id];
      if (!runExists) {
        const reportRun = {
          id: selectedRun.id,
          name: selectedRun.label,
          status: props.activeModeler?.modelRuns?.items?.find(run => run.id === selectedRun.id)?.runStatus || 'Unknown',
        };
        
        // Add report run and fetch its assets using new Redux action
        try {
          await props.onAddReportRunAndFetchAssets(props.activeModeler?.id, reportRun, 100, null);
        } catch (error) {
          console.error("Error adding report run and fetching assets:", error);
        }
      } else {
        console.log(`ModelRun ${selectedRun.label} already selected, skipping`);
      }
    }
  };

  const handleRemoveModelRun = (runToRemove) => {
    props.onRemoveReportRun(runToRemove.id);
    if (selectedModelRun && selectedModelRun.id === runToRemove.id) {
      setSelectedModelRun(null);
    }
  };

  const handleVtszAssetSelect = (runId, params) => {
    props.onUpdateReportRunSelectedAssets(runId, 'selectedVtszAssets', params.value || []);
  };

  const handleMediaAssetSelect = (runId, params) => {
    props.onUpdateReportRunSelectedAssets(runId, 'selectedMediaAssets', params.value || []);
  };



  const handlePublish = async () => {
    console.log("Executing report pipeline with model runs and assets:", selectedModelRuns);
    
    if (selectedModelRuns.length === 0) {
      console.warn("No model runs selected for report execution");
      return;
    }

    if (!props.activeModeler?.id) {
      console.error("No active modeler found");
      return;
    }

    try {
      // Call the new thunk to execute the report pipeline
      await props.onExecuteReportPipeline(
        props.activeModeler.id,
        selectedModelRuns
      );
             console.log("Report pipeline executed successfully");
       // The generatedReportId will be available in props.activeModeler.generatedReportId
    } catch (error) {
      console.error("Error executing report pipeline:", error);
      // The error will be handled by the Redux state and displayed in the UI
    }
  };

  return (
    <Block padding="scale600">
      <Block display="flex" justifyContent="space-between" alignItems="center" marginBottom="scale600">
        <HeadingMedium>Reports</HeadingMedium>
        <Button
          size={SIZE.compact}
          kind={KIND.primary}
          shape={SHAPE.pill}
          onClick={handlePublish}
          disabled={selectedModelRuns.length === 0 || props.publishReportStatus === "STARTED"}
          isLoading={props.publishReportStatus === "STARTED"}
        >
          {props.publishReportStatus === "STARTED" ? "Executing..." : "Execute Report"}
        </Button>
      </Block>

      <Tabs
        activeKey={activeKey}
        onChange={({ activeKey }) => setActiveKey(activeKey)}
        activateOnFocus
      >
        <Tab title="Report Parameters">
          <Block paddingTop="scale600">
            <FormControl
              id="modeler-model-runs"
              label={() => "Select Model Runs"}
            >
              <Block>
                <Select
                  size={SSIZE.compact}
                  options={modelRunOptions}
                  value={selectedModelRun ? [selectedModelRun] : []}
                  onChange={handleModelRunSelect}
                  placeholder="Choose a model run to fetch its assets..."
                  clearable={false}
                  isLoading={props.fetchReportAssetsInProgress}
                />
                {props.fetchReportAssetsInProgress && (
                  <Block marginTop="scale200">
                    <ProgressBar
                      infinite
                      size="small"
                      overrides={{
                        Bar: {
                          style: {
                            height: '4px',
                          },
                        },
                      }}
                    />
                  </Block>
                )}
              </Block>
            </FormControl>
            
            {props.fetchReportAssetsInProgress && (
              <Block marginTop="scale300" color="contentTertiary">
                Fetching assets for selected ModelRun...
              </Block>
            )}
            
            {props.fetchReportAssetsError && (
              <Block marginTop="scale300" color="contentNegative">
                {props.fetchReportAssetsErrorMessage}
              </Block>
            )}





            {selectedModelRuns.length > 0 && (
              <Block marginTop="scale600">
                <FormControl label={() => "Selected Model Runs"}>
                  <Block
                    $style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '16px',
                      backgroundColor: '#f8f9fa'
                    }}
                  >
                    {selectedModelRuns.map((run, index) => {
                      const runAssets = run.assets || [];
                      
                      return (
                        <Block
                          key={run.id}
                          marginBottom={index < selectedModelRuns.length - 1 ? "scale400" : "0"}
                        >
                          <ModelRunItem
                            run={run}
                            runAssets={runAssets}
                            onRemoveModelRun={handleRemoveModelRun}
                            onVtszAssetSelect={handleVtszAssetSelect}
                            onMediaAssetSelect={handleMediaAssetSelect}
                          />
                        </Block>
                      );
                    })}
                  </Block>
                </FormControl>
              </Block>
            )}
          </Block>
        </Tab>

        <Tab title="Markdown Content">
                      <Block paddingTop="scale600">
            <FormControl
              id="modeler-reports-textarea"
              label={() => (
                <Block display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  <Block>
                    {isContentModified 
                      ? "Report Content (Markdown) • Modified"
                      : "Report Content (Markdown)"
                    }
                  </Block>
                  <Button
                    size={SIZE.compact}
                    kind={isContentModified ? KIND.primary : KIND.secondary}
                    onClick={handleSave}
                    disabled={!isContentModified || isSaving}
                    isLoading={isSaving}
                    startEnhancer={() => <FaSave />}
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </Button>
                </Block>
              )}
              overrides={{
                Label: {
                  style: {
                    color: isContentModified ? '#FFA500' : undefined,
                    fontWeight: isContentModified ? 'bold' : undefined,
                  },
                },
              }}
            >
              <Textarea
                overrides={{
                  Input: {
                    style: {
                      height: "600px",
                      fontFamily: "monospace",
                      fontSize: "14px",
                    },
                  },
                }}
                size={TSIZE.mini}
                value={reportContent}
                onChange={handleContentChange}
                onBlur={handleBlur}
                placeholder="Enter your report content in Markdown format..."
              />
            </FormControl>
          </Block>
        </Tab>
      </Tabs>
    </Block>
  );
}

const mapStateToProps = (state) => {
  return {
    activeModeler: state.modeler.activeModeler,
    reportRuns: state.modelRun?.reportRuns || {},
    fetchReportAssetsInProgress: state.modelRun?.fetchReportAssetsInProgress || false,
    fetchReportAssetsError: state.modelRun?.fetchReportAssetsError || false,
    fetchReportAssetsErrorMessage: state.modelRun?.fetchReportAssetsErrorMessage || "",
    publishReportStatus: state.modeler?.publishReportStatus || "NONE",
    publishReportError: state.modeler?.publishReportError || false,
    publishReportErrorMessage: state.modeler?.publishReportErrorMessage || "",
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateActiveModelerProperties: (modelerId, modeler) => {
      dispatch(updateModeler(modelerId, modeler));
    },
    onAddReportRunAndFetchAssets: (modelerId, reportRun, pageSize, nextToken) => {
      return dispatch(addReportRunAndFetchAssets(modelerId, reportRun, pageSize, nextToken));
    },
    onRemoveReportRun: (modelRunId) => {
      dispatch(ModelRunActions.removeReportRun(modelRunId));
    },
    onUpdateReportRunSelectedAssets: (modelRunId, assetType, selectedAssets) => {
      dispatch(ModelRunActions.updateReportRunSelectedAssets(modelRunId, assetType, selectedAssets));
    },
    onExecuteReportPipeline: (modelerId, modelRuns) => {
      return dispatch(executeReportPipeline(modelerId, modelRuns));
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ModelerReportsView); 